import { Title } from '@angular/platform-browser';
import { Component, OnInit, ChangeDetectionStrategy, inject } from '@angular/core';
import {
  combineLatest,
  EMPTY,
  filter,
  finalize,
  firstValueFrom,
  map,
  Observable,
  of,
  scan,
  startWith,
  switchMap,
  tap,
  withLatestFrom,
} from 'rxjs';
import { IProjectsListingVM } from '@core/state/entities/project/project-summary.viewmodel';
import { createSelector, Store } from '@ngrx/store';
import { selectProjectListingsAreLoading } from '@app/core/state/ui/project-index/project-index-ui.selectors';
import {
  selectCurrentUser,
  selectCurrentUserId,
} from '@app/core/auth/auth.selectors';
import { SyrfDialogService } from '@shared/dialog/syrf-dialog.service';
import { openInfoConfirmation } from '@shared/dialog/syrf-confirmations';
import {
  CREATE_PROJECT_WIZARD_TITLE,
  CreateProjectWizardComponent,
} from './dialogs/create-project-wizard/create-project-wizard.component';
import { IUser } from '@app/core/auth/user.entity';
import _ from 'lodash-es';
import {
  FormControl,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import { loadLunr } from '@app/shared/utils/lunr-loader';
import { guidToBase64 } from '@core/utils/guid-encode';
import { selectProjectIsLoading } from '@core/state/ui/project/project-ui.selectors';
import { ActivatedRouteSnapshot, Router } from '@angular/router';
import { projectDetailActions } from '@core/actions/project-detail.actions';
import { entityKey } from '@core/state/entities/entity-name-map';
import { selectEntityMap } from '@core/state/entities/entity.selectors';
import { debounceSync, throwErrorExp } from '@core/actions/util';
import { fromPromise } from 'rxjs/internal/observable/innerFrom';
import { IProject } from '@core/state/entities/project/project.entity';
import { getRouteSnapshotForCachedComponent } from '@core/utils/router.utils';
import {
  getJoinProjectDialogMessages,
  IJoinProjectDialogData,
  InvitationStatus,
  JoinProjectDialogComponent,
  JoinProjectDialogResult,
  JoinProjectState,
  projectAccessApprovalRequiredText,
  projectAccessAutoApprovalText,
} from '@app/project-index/dialogs/join-project-dialog/join-project-dialog.component';
import {
  filterAdminProjectsVms,
  filterInvitedProjectsVms,
  filterJoinRequestedProjectsVms,
  filterMyProjectsVms,
  filterNonAdminProjectsVms,
  filterOtherProjectsVms,
  filterPendingProjectVms,
  selectProjectListings,
} from '@core/state/entities/project/project-summary.viewmodel.selectors';
import { IHeadingFilter } from '@app/project-index/filter-list/filter-list.component';
import { IInvitation } from '@core/state/entities/invitation/invitation.entity';
import { selectInvitationMapByToken } from '@core/state/entities/invitation-secure-details/invitation-secure-details.selectors';
import { IInfoConfirmationDialogData } from '@shared/dialogs/info-confirmation-dialog/info-confirmation-dialog.component';
import { v4 as uuid } from 'uuid';
import { Actions, ofType } from '@ngrx/effects';
import {
  ProjectIndexState,
  ProjectIndexStore,
  TokenProjectStatus,
} from '@app/project-index/project-index.store';
import { selectProjectById } from '@core/state/entities/project/project.selectors';
import { FilterListComponent } from './filter-list/filter-list.component';
import { MatTabsModule } from '@angular/material/tabs';
import { MatTooltipModule } from '@angular/material/tooltip';
import { NgClass, AsyncPipe } from '@angular/common';
import { MatInputModule } from '@angular/material/input';
import { GridModule } from '@ngbracket/ngx-layout/grid';
import { FlexLayoutModule } from '@ngbracket/ngx-layout';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { RgBodyDirective } from '../shared/layout/rg-body.directive';
import { from } from 'rxjs';

export interface ProjectListings {
  otherProjects: IProjectsListingVM[];
  otherProjectCount: number;
  pendingProjects: IProjectsListingVM[];
  pendingProjectsCount: number;
  myProjects: IProjectsListingVM[];
  myProjectCount: number;
}

export const TokenRetrievalResponse = {
  passthrough: 'passthrough',
  notFound: 'notFound',
  retrieved: 'retrieved',
} as const;
export type TokenRetrievalResponse =
  (typeof TokenRetrievalResponse)[keyof typeof TokenRetrievalResponse];

export type TokenRetrievalResult =
  | {
      response: typeof TokenRetrievalResponse.passthrough;
    }
  | {
      response: typeof TokenRetrievalResponse.notFound;
      error: string;
    }
  | {
      response: typeof TokenRetrievalResponse.retrieved;
      token: string;
      projectId: string;
    };

interface CancellationToken {
  cancel: () => void;
  isCancelled: boolean;
  completed: boolean;
}
const newCancellationToken = (): CancellationToken => ({
  cancel() {
    this.isCancelled = true;
  },
  isCancelled: false,
  completed: false,
});
const requestCancelledConfirmationMessage = `Your join request has been successfully cancelled.\nThe project has been removed from your list of pending projects.`;
const errorCancellingJoinRequestMessage = `There was an problem cancelling the join request.\nPlease try again later or get in touch if the problem persists.`;
const joinRequestCancellationConfirmationQuestion =
  'Are you sure you want to cancel your request to join this project?';
const joinRequestCancellationTitle = 'Cancel request to join project';
const invitationInvalidMessage =
  'The invitation you are trying to use is not valid or has expired.';
const invitationSuccessfullyDeclined = `The invitation has been successfully declined. \nThe project has been removed from your list of pending projects.`;
const errorDecliningInvitationMessage = `An error occurred declining the invitation`;
const joinRequestSentConfirmationMessage =
  'Your request to join the project has been successfully sent.\nThe project will appear in your list of pending projects and you will be notified when your request has been approved.';
const joinRequestErrorMessage = `There was a problem requesting to join the project.\nPlease try again later or get in touch if the problem persists.`;
@Component({
  selector: 'app-project-index',
  templateUrl: './project-index.component.html',
  styleUrls: ['./project-index.component.scss'],
  providers: [ProjectIndexStore],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    RgBodyDirective,
    MatProgressBarModule,
    MatButtonModule,
    MatIconModule,
    FlexLayoutModule,
    GridModule,
    MatInputModule,
    FormsModule,
    ReactiveFormsModule,
    NgClass,
    MatTooltipModule,
    MatTabsModule,
    FilterListComponent,
    AsyncPipe,
  ],
})
export class ProjectIndexComponent implements OnInit {
  private readonly _syrfDialog = inject(SyrfDialogService);
  filteredProjects$: Observable<IProjectsListingVM[]>;
  projectsMap$: Observable<{ [id: string]: IProjectsListingVM }>;
  user$: Observable<IUser | null>;
  filteredMyProjects$: Observable<IProjectsListingVM[]>;
  filteredPublicProjects$: Observable<IProjectsListingVM[]>;
  publicProjectCount$: Observable<number>;
  myProjectCount$: Observable<number>;
  filteredProjectListings$: Observable<ProjectListings>;
  searchControl: FormControl<string> = new FormControl('', {
    nonNullable: true,
  });
  projectsLoading$: Observable<boolean>;
  projectLoading$: Observable<boolean>;
  possessiveName$: Observable<string | null>;
  myProjectFilters: IHeadingFilter[] = [
    {
      subheading: 'Admin',
      filterFn: filterAdminProjectsVms,
    },
    {
      subheading: 'Reviewer',
      filterFn: filterNonAdminProjectsVms,
    },
  ];
  pendingProjectFilters: IHeadingFilter[] = [
    {
      subheading: 'Invitations',
      filterFn: filterInvitedProjectsVms,
    },
    {
      subheading: 'Join Requests',
      filterFn: filterJoinRequestedProjectsVms,
    },
  ];
  async openProject(projectId: string) {
    console.log('open project', projectId);
    const projectMap = await firstValueFrom(
      this._store.select(selectEntityMap(entityKey.projects))
    );
    const project =
      projectMap[projectId] ?? throwErrorExp(new Error('Project not found'));
    if (project.isMember) {
      return this._router.navigate(['/projects', projectId, 'detail']);
    }
    return this._router.navigate(['/projects', projectId]);
  }
  private readonly _titleService = inject(Title);
  private readonly _store = inject(Store);
  private readonly _router = inject(Router);
  private readonly _projectIndexStore = inject(ProjectIndexStore);
  private readonly _actions$ = inject(Actions);
  constructor() {
    this.publicProjectCount$ = this._store.select(
      selectProjectCount(filterOtherProjectsVms)
    );

    this.myProjectCount$ = this._store.select(
      selectProjectCount(filterMyProjectsVms)
    );

    this.projectsLoading$ = this._store.select(selectProjectListingsAreLoading);
    this.projectLoading$ = this._store.select(selectProjectIsLoading);

    this.filteredProjects$ = combineLatest([
      this._store.select(selectProjectListings),
      this.searchControl.valueChanges.pipe(startWith(this.searchControl.value)),
      this._projectIndexStore.select(
        (state: ProjectIndexState) => state.projectSearchIndex
      ),
    ]).pipe(
      filter(([projects]) => !!projects),
      map(([projects, searchTerm, projectSearchIndex]) => {
        const listingMap = _.mapKeys(projects, (p) => p.id);
        const queryString =
          searchTerm +
          (searchTerm && searchTerm[searchTerm.length - 1] !== ' ' ? '*' : '');
        const escapedQueryString = queryString.replace(':', '\\:');
        const searchR = projectSearchIndex?.search(escapedQueryString);
        return searchTerm && searchR
          ? searchR
              .map((r) => listingMap[r.ref] as IProjectsListingVM)
              .filter((r) => !!r)
          : _.sortBy(
              projects,
              (p) => p.membershipDate && -new Date(p.membershipDate),
              (p) => p.name
            );
      })
    );
    this.filteredMyProjects$ = this.filteredProjects$.pipe(
      map((pl) => pl.filter((p) => p.isMember && p.isDisabled !== true))
    );
    this.filteredPublicProjects$ = this.filteredProjects$.pipe(
      map((pl) => pl.filter((p) => p.isPublic))
    );
    this.filteredProjectListings$ = combineLatest([
      this.filteredProjects$,
      this._store.select(selectProjectListings),
    ]).pipe(
      map(([filteredListings, allListings]) => {
        return {
          otherProjects: filterOtherProjectsVms(filteredListings),
          otherProjectCount: filterOtherProjectsVms(allListings).length,
          pendingProjects: [
            ...filterInvitedProjectsVms(filteredListings),
            ...filterJoinRequestedProjectsVms(filteredListings),
          ],
          pendingProjectsCount: filterPendingProjectVms(allListings).length,
          myProjects: [
            ...filterAdminProjectsVms(filteredListings),
            ...filterNonAdminProjectsVms(filteredListings),
          ],
          myProjectCount: filterMyProjectsVms(allListings).length,
        };
      })
    );

    this._projectIndexStore.patchState(
      this._store.select(selectProjectListings).pipe(
        switchMap((prs) =>
          from(loadLunr()).pipe(
            map((lunr) => {
              const builder = new lunr.Builder();
              builder.ref('id');
              builder.field('name');
              builder.field('keywords', { boost: 1.3 });
              builder.field('id', { boost: 1.5 });
              builder.field('idBase64', { boost: 1.5 });
              prs.forEach((p) => {
                try {
                  const base64Id = guidToBase64(p.id);
                  builder.add({
                    ...p,
                    idBase64: base64Id,
                  });
                } catch {
                  builder.add(p);
                }
              });
              return { projectSearchIndex: builder.build() };
            })
          )
        )
      )
    );

    this.projectsMap$ = this.filteredProjects$.pipe(
      map((s) => _.keyBy(s, (p) => p.id))
    );
    this.user$ = this._store.select(selectCurrentUser).pipe(filter((u) => !!u));
    this._titleService.setTitle('Projects - SyRF');
    this.possessiveName$ = this.user$.pipe(
      map((u) => {
        const name = u?.fullName.preferred ?? u?.fullName.first ?? null;
        if (!name) {
          return null;
        }
        return name.substr(name.length - 1) === 's' ? `${name}'` : name + "'s";
      })
    );

    this._projectIndexStore.effect(() =>
      getRouteSnapshotForCachedComponent(
        this._router,
        ProjectIndexComponent
      ).pipe(
        switchMap((route: ActivatedRouteSnapshot) => {
          return route.paramMap.has('projectId') ||
            route.paramMap.has('invitationToken')
            ? fromPromise(this.checkRouteInvitationToken(route)).pipe(
                tap((result) => {
                  if (result.response === TokenRetrievalResponse.retrieved) {
                    this._router.navigate([
                      '/projects',
                      result.projectId,
                      { invitationToken: result.token },
                    ]);
                  } else if (
                    result.response === TokenRetrievalResponse.notFound
                  ) {
                    this._router.navigate(['/projects']);

                    const data: IInfoConfirmationDialogData = {
                      title: 'Invitation not found',
                      text: invitationInvalidMessage,
                    };
                    openInfoConfirmation(this._syrfDialog, data);
                  }
                }),
                filter(
                  (result) =>
                    result.response === TokenRetrievalResponse.passthrough
                ),
                map(() => route),

                map((route) => route.paramMap),
                map(
                  (paramMap) =>
                    [
                      paramMap.get('projectId'),
                      paramMap.get('invitationToken'),
                    ] as [string | undefined, string | undefined]
                ),
                withLatestFrom(
                  this._store.select(selectEntityMap(entityKey.projects)),
                  this._store.select(selectInvitationMapByToken)
                ),
                map(
                  ([
                    [projectId, invitationToken],
                    projectMap,
                    invitationByTokenMap,
                  ]) => {
                    const invitation = invitationToken
                      ? invitationByTokenMap[invitationToken]
                      : undefined;
                    const project = projectId
                      ? projectMap[projectId]
                      : invitation
                        ? projectMap[invitation.projectId]
                        : undefined;
                    return [
                      project,
                      invitation,
                      invitationToken,
                      projectId,
                    ] as [
                      IProject | undefined,
                      IInvitation | undefined,
                      string | undefined,
                      string | undefined,
                    ];
                  }
                ),
                tap(([project, , , projectId]) => {
                  if (!project && projectId) {
                    this._router.navigate(['/not-found']);
                  }
                  if (project?.isMember) {
                    this._router.navigate(['/projects', project.id, 'detail']);
                  }
                }),
                // filter(([project]) => project && !project.isMember),
                switchMap(([project, invitation, invitationToken]) => {
                  let ignoreFutureUpdates = false;
                  if (ignoreFutureUpdates) {
                    return EMPTY;
                  }
                  if (!project) {
                    return EMPTY;
                  }
                  const project$ = this._store.select(
                    selectProjectById(project.id)
                  );
                  const invitationFromToken$ = this._store
                    .select(selectInvitationMapByToken)
                    .pipe(
                      map((invitationMap) =>
                        invitationToken
                          ? invitationMap[invitationToken]
                          : undefined
                      )
                    );
                  const dialogData$ = combineLatest([
                    project$,
                    invitationFromToken$,
                  ]).pipe(
                    debounceSync(),
                    switchMap(([project, invitation]) =>
                      ignoreFutureUpdates || !project || project?.isMember
                        ? EMPTY
                        : fromPromise(
                            this.getLaunchProjectDialogData(
                              project,
                              invitation,
                              () => {
                                ignoreFutureUpdates = true;
                              }
                            )
                          )
                    )
                  );
                  return dialogData$.pipe(
                    scan(
                      ([prev, joinRequestJustSent], curr) => {
                        return [
                          curr,
                          prev?.joinProjectState ===
                            JoinProjectState.requestRequired &&
                            curr?.joinProjectState ===
                              JoinProjectState.requestPending,
                        ] as const;
                      },
                      [undefined, false] as [
                        IJoinProjectDialogData | undefined,
                        boolean,
                      ]
                    ),

                    switchMap(([dialogData, joinRequestJustSent]) => {
                      const abortController = new AbortController();
                      const abortSignal = abortController.signal;
                      if (!dialogData) {
                        return of(false);
                      }
                      if (joinRequestJustSent) {
                        ignoreFutureUpdates = false;
                        const sentData: IInfoConfirmationDialogData = {
                          title: 'Join Request Sent',
                          text: 'Your join request has been sent. You will be notified when it is approved.',
                          hideCancel: true,
                        };
                        return openInfoConfirmation(this._syrfDialog, sentData)
                          .afterClosed()
                          .pipe(map(() => false));
                      }
                      return fromPromise(
                        this.launchJoinProjectDialog(
                          dialogData,
                          abortSignal,
                          () => {
                            ignoreFutureUpdates = true;
                          }
                        )
                      ).pipe(
                        finalize(() => abortController.abort()),
                        map(
                          (result) =>
                            result === JoinProjectDialogResult.joinProject
                        )
                      );
                    }),
                    tap((result) =>
                      result
                        ? this._router.navigate(['projects', project.id])
                        : this._router.navigate(['projects'])
                    )
                  );
                })
              )
            : of(undefined);
        })
      )
    );
  }

  async checkRouteInvitationToken(
    route: ActivatedRouteSnapshot
  ): Promise<TokenRetrievalResult> {
    const invitationToken = route.paramMap.get('invitationToken');
    if (!invitationToken) {
      return {
        response: TokenRetrievalResponse.passthrough,
      };
    }
    const tokenState = await firstValueFrom(
      this._projectIndexStore.select((state) => state.tokenProjectState)
    );
    if (
      tokenState.status !== TokenProjectStatus.idle &&
      tokenState.token === invitationToken
    ) {
      return tokenState.status === TokenProjectStatus.tokenProjectNotFound
        ? throwErrorExp('token project should not be not found at this point')
        : tokenState.status === TokenProjectStatus.tokenProjectLoaded
          ? route.url[0].path !== 'invitations'
            ? {
                response: TokenRetrievalResponse.passthrough,
              }
            : throwErrorExp('token project should not be loaded at this point')
          : throwErrorExp('token project should not be fetching at this point');
    }
    const correlationId = uuid();
    // this._store.dispatch(projectDetailActions.projectListingsPageLoaded());
    // await firstValueFrom(
    //   this._actions$.pipe(ofType(projectDetailActions.projectListingsLoaded))
    // );
    this._store.dispatch(
      projectDetailActions.retrieveProjectForInvitation({
        token: invitationToken,
        correlationId,
      })
    );
    this._projectIndexStore.patchState({
      tokenProjectState: {
        status: TokenProjectStatus.fetchingProject,
        token:
          invitationToken ??
          throwErrorExp('token should be defined at this point '),
      },
    });
    const result = await firstValueFrom(
      this._actions$.pipe(
        ofType(
          projectDetailActions.projectForInvitationRetrieved,
          projectDetailActions.errorRetrievingProjectForInvitation
        ),
        filter((action) => action.correlationId === correlationId),
        tap((action) => {
          this._projectIndexStore.patchState({
            tokenProjectState:
              action.type ===
              projectDetailActions.projectForInvitationRetrieved.type
                ? {
                    status: TokenProjectStatus.tokenProjectLoaded,
                    token:
                      invitationToken ??
                      throwErrorExp('token should be defined at this point '),
                    projectId: action.projectId,
                  }
                : {
                    status: TokenProjectStatus.tokenProjectNotFound,
                    error: action.error,
                    token:
                      invitationToken ??
                      throwErrorExp('token should be defined at this point '),
                  },
          });
        }),
        map((action) =>
          action.type ===
          projectDetailActions.projectForInvitationRetrieved.type
            ? {
                projectId: action.projectId,
                token: invitationToken,
                response: TokenRetrievalResponse.retrieved,
              }
            : ({
                response: TokenRetrievalResponse.notFound,
              } as TokenRetrievalResult)
        )
      )
    );
    return result;
  }

  async getLaunchProjectDialogData(
    project: IProject,
    invitation: IInvitation | undefined,
    onTerminalAction: () => void
  ): Promise<IJoinProjectDialogData | undefined> {
    if (!project) {
      return undefined;
    }
    const investigatorMap = await firstValueFrom(
      this._store.select(selectEntityMap(entityKey.investigators))
    );
    const invitationMap = await firstValueFrom(
      this._store.select(selectEntityMap(entityKey.invitations))
    );
    const joinRequestMap = await firstValueFrom(
      this._store.select(selectEntityMap(entityKey.joinRequests))
    );
    const currentUserId = await firstValueFrom(
      this._store.select(selectCurrentUserId)
    );
    const invitationIsMismatched =
      (invitation ?? false) && invitation?.inviteeId !== currentUserId;
    if (!project.invitation && invitation && !invitationIsMismatched) {
      throw new Error('Matching invitation not linked to project entity');
    }
    const invitationStatus =
      !invitationIsMismatched &&
      invitationMap[project.invitation]?.inviteeId === currentUserId
        ? InvitationStatus.claimedByThisAccount
        : invitationIsMismatched
          ? invitation?.inviteeId
            ? InvitationStatus.claimedByAnotherAccount
            : InvitationStatus.unclaimed
          : undefined;
    const invitationInfo = invitationStatus && {
      state: invitationStatus,
      email: (invitation ?? invitationMap[project.invitation])?.inviteeEmail,
      message: (invitation ?? invitationMap[project.invitation])?.message,
    };

    const dialogData: Pick<
      IJoinProjectDialogData,
      | 'projectAccess'
      | 'positiveActivity'
      | 'recordReason'
      | 'negativeActivity'
      | 'dialogIcon'
      | 'joinProjectState'
    > = project.invitation
      ? {
          joinProjectState: JoinProjectState.invited,
          projectAccess: 'Invitation',
          dialogIcon: 'mail',
          positiveActivity: {
            buttonText: 'Accept and join',
            dispatchAction: ({ correlationId, message }) =>
              projectDetailActions.respondToInvitation({
                projectId: project.id,
                accepted: true,
                correlationId,
              }),
            successActions: projectDetailActions.invitationResponseReceived,
            errorActions: projectDetailActions.errorRespondingToInvitation,
            friendlyErrorMessage: `An error occurred accepting the invitation`,
          },
          negativeActivity: {
            buttonText: 'Decline',
            dispatchAction: ({ correlationId, message }) =>
              projectDetailActions.respondToInvitation({
                projectId: project.id,
                accepted: false,
                correlationId,
              }),
            successActions: projectDetailActions.invitationResponseReceived,
            errorActions: projectDetailActions.errorRespondingToInvitation,
            successMessage: invitationSuccessfullyDeclined,
            friendlyErrorMessage: errorDecliningInvitationMessage,
            confirmationDialog: () => ({
              title: 'Decline invitation',
              text: 'Are you sure you want to decline this invitation?',
              informationBox: {
                text: 'You will not be able to accept this invitation later.',
                warning: true,
              },
            }),
          },
        }
      : project.joinRequest
        ? {
            joinProjectState: JoinProjectState.requestPending,
            projectAccess: projectAccessApprovalRequiredText,
            dialogIcon: 'pending',
            negativeActivity: {
              buttonText: 'Cancel request',
              dispatchAction: ({ correlationId, message }) =>
                projectDetailActions.cancelRequestToJoinProject({
                  projectId: project.id,
                  correlationId,
                }),
              successActions:
                projectDetailActions.requestToJoinProjectCancelled,
              errorActions:
                projectDetailActions.errorCancellingRequestToJoinProject,
              successMessage: requestCancelledConfirmationMessage,
              friendlyErrorMessage: errorCancellingJoinRequestMessage,
              confirmationDialog: () => ({
                title: joinRequestCancellationTitle,
                text: joinRequestCancellationConfirmationQuestion,
              }),
            },
          }
        : {
            joinProjectState: project.autoApproveJoinRequests
              ? JoinProjectState.autoApproval
              : JoinProjectState.requestRequired,
            ...(project.autoApproveJoinRequests
              ? {
                  projectAccess: projectAccessAutoApprovalText,
                  recordReason: false,
                }
              : {
                  projectAccess: projectAccessApprovalRequiredText,
                  recordReason: true,
                }),
            positiveActivity: {
              buttonText: project.autoApproveJoinRequests
                ? 'Join'
                : 'Request to join',
              dispatchAction: ({ correlationId, message }) =>
                projectDetailActions.requestToJoinProject({
                  projectId: project.id,
                  message: message,
                  correlationId,
                }),
              successActions: [
                projectDetailActions.requestToJoinProjectResponseReceived,
                projectDetailActions.requestToJoinProjectResponseReceivedAndGranted,
              ],
              errorActions: projectDetailActions.errorRequestingToJoinProject,
              friendlyErrorMessage: joinRequestErrorMessage,
              successMessage: project.autoApproveJoinRequests
                ? undefined
                : joinRequestSentConfirmationMessage,
            },
          };
    const data: IJoinProjectDialogData = {
      projectId: project.id,
      projectName: project.name,
      owner: investigatorMap[project.ownerId].fullName,
      description: project.description,
      dialogIcon: 'email',
      invitationInfo,
      ...dialogData,
      ...getJoinProjectDialogMessages(
        project,
        invitationMap,
        investigatorMap,
        joinRequestMap
      ),
      onTerminalAction,
    };
    return data;
  }

  async launchJoinProjectDialog(
    data: IJoinProjectDialogData,
    abortSignal: AbortSignal,
    onTerminalAction: () => void
  ): Promise<JoinProjectDialogResult | undefined> {
    return new Promise<JoinProjectDialogResult | undefined>((resolve) => {
      const dialogRef = this._syrfDialog.open<
        JoinProjectDialogComponent,
        IJoinProjectDialogData,
        JoinProjectDialogResult
      >(JoinProjectDialogComponent, {
        mode: 'adaptive',
        title: data.projectName,
        data,
      });
      const onAbort = () => {
        dialogRef.close(JoinProjectDialogResult.autoClose);
      };
      abortSignal.addEventListener('abort', onAbort);
      return firstValueFrom(dialogRef.afterClosed()).then((result) => {
        abortSignal.removeEventListener('abort', onAbort);
        resolve(result);
      });
    });
  }
  ngOnInit() {
    this._store.dispatch(
      projectDetailActions.projectListingsPageLoaded({
        correlationId: uuid(),
      })
    );
  }

  clearSearchBar() {
    this.searchControl.setValue('');
  }

  onProjectCreate() {
    this._syrfDialog.open(CreateProjectWizardComponent, {
      mode: 'adaptive',
      title: CREATE_PROJECT_WIZARD_TITLE,
      width: '650px',
    });
  }
  refreshListings() {
    this._store.dispatch(
      projectDetailActions.projectListingsPageLoaded({
        correlationId: uuid(),
      })
    );
  }
}
export const selectProjectCount = (
  projectsFilter: (projects: IProjectsListingVM[]) => IProjectsListingVM[] = (
    p
  ) => p
) =>
  createSelector(
    selectProjectListings,
    (projects) => projectsFilter(projects).length
  );
